<script lang="ts">
export default {
    name: 'BaseStep',
};
</script>

<script setup lang="ts">
import { computed, getCurrentInstance, onMounted, onUnmounted } from 'vue';
import { BaseSteps } from '@/components/base-components/components/steps/context';
import { DIRECTIONS } from '@/components/base-components/utils/direction';
import BaseIcon from '@/components/base-components/components/base-icon.vue';
import { requireInject } from '@/components/base-components/utils/require-inject';

const props = defineProps<{
    title?: string;
    description?: string;
    icon?: string | object;
    clickable?: boolean;
    anchorClass?: unknown;
}>();

const emit = defineEmits<{
    (e: 'click'): void;
}>();

const { steps, active, marker, centered: parentCentered, direction } = requireInject(BaseSteps);

const centered = computed(() => parentCentered.value || props.clickable);

const current = getCurrentInstance();

onMounted(() => {
    if (!current || steps.value.includes(current)) return;

    steps.value.push(current);
});

onUnmounted(() => {
    if (!current) return;

    const index = steps.value.indexOf(current);

    if (index >= 0) {
        steps.value.splice(index, 1);
    }
});

const index = computed(() => steps.value.indexOf(current));
const position = computed(() => index.value + 1);

const hasActive = computed(() => active.value >= 0);

const isPrevious = computed(() => hasActive.value && index.value < active.value);
const isActive = computed(() => hasActive.value && index.value === active.value);
const isNext = computed(() => hasActive.value && index.value > active.value);

const isHorizontal = computed(() => direction.value === DIRECTIONS.HORIZONTAL);
const firstStep = computed(() => position.value === 1);
const lastStep = computed(() => position.value === steps.value.length);

function onClick(e: MouseEvent) {
    if (!props.clickable) return;

    emit('click', e);
}
</script>

<template>
    <div
        class="flex items-start"
        :class="[
            {
                'cursor-pointer': clickable,
                'flex-1': !lastStep || centered || clickable,
                'justify-center': isHorizontal && (centered || clickable),
            },
        ]"
        @click="onClick"
    >
        <div
            class="flex gap-1"
            :class="[
                {
                    'flex-col': isHorizontal,
                    'items-start': isHorizontal && !centered,
                    'items-center': (isHorizontal && centered) || !isHorizontal,
                }
            ]"
        >
            <div
                :class="anchorClass"
                :style="{
                    'anchor-name': `--base-step-${position}`,
                }"
            >
                <slot :is-active="isActive" :is-previous="isPrevious" :is-next="isNext" :position="position">
                    <div
                        class="border-2 rounded-full m-1"
                        :class="[
                            isPrevious ? 'text-white border-secondary-950 bg-secondary-950' : undefined,
                            (isNext && !clickable) ? 'text-gray-400 border-gray-300' : 'text-secondary-950 border-secondary-950',
                        ]"
                    >
                        <slot name="pill" :is-active="isActive" :is-previous="isPrevious" :is-next="isNext" :position="position">
                            <div
                                class="flex items-center justify-center size-6"
                                :class="[
                                    isActive ? 'font-semibold' : 'font-medium'
                                ]"
                            >
                                <template v-if="marker">
                                    <base-icon class="text-base" name="check" v-if="isPrevious" />
                                    <div v-else-if="isActive" class="size-2 rounded-full bg-secondary-950" />
                                </template>
                                <template v-else>
                                    {{ position }}
                                </template>
                            </div>
                        </slot>
                    </div>
                </slot>
            </div>

            <div
                v-if="title || description"
                class="flex flex-col justify-center"
            >
                <slot name="title">
                    <div
                        v-if="title || $slots.titleContent"
                        :class="[
                            (isNext && !clickable) ? 'text-gray-400' : 'text-secondary-950',
                            isActive ? 'font-semibold' : 'font-medium'
                        ]"
                    >
                        <slot name="title-content" :title="title">
                            <span v-text="title" />
                        </slot>
                    </div>
                </slot>

                <slot name="description">
                    <div
                        v-if="description || $slots.descriptionContent"
                        class="text-sm"
                        :class="[
                            (isNext && !clickable) ? 'text-gray-400' : 'text-gray-500',
                        ]"
                    >
                        <slot name="description-content" :description="description">
                            <span v-text="description" />
                        </slot>
                    </div>
                </slot>
            </div>
        </div>

        <div
            v-if="!firstStep"
            class="absolute"
            :class="[
                (isActive || isPrevious || !hasActive) ? 'bg-secondary-950' : 'bg-gray-300',
                {
                    'h-[2px] -mt-px': isHorizontal,
                    'w-[2px] -ml-px': !isHorizontal,
                }
            ]"
            :style="{
                top: `anchor(--base-step-${position - 1} ${ isHorizontal ? 'center' : 'bottom' })`,
                left: `anchor(--base-step-${position - 1} ${ isHorizontal ? 'right' : 'center' })`,
                bottom: `anchor(--base-step-${position} ${ isHorizontal ? 'center' : 'top' })`,
                right: `anchor(--base-step-${position} ${ isHorizontal ? 'left' : 'center' })`,
            }"
        />
    </div>
</template>
